import { readFile, writeFile, readdir, mkdir } from 'node:fs/promises'
import { join } from 'node:path'
import { homedir } from 'node:os'
import { existsSync } from 'node:fs'
import type { IPty } from 'node-pty'

export type SessionEventType = 'stdout' | 'stderr' | 'stdin' | 'command' | 'note'

export interface SessionEvent {
  timestamp: number
  type: SessionEventType
  data: string
}

export interface RecordingData {
  sessionId: string
  startedAt: string
  events: SessionEvent[]
}

const RECORDINGS_DIR = join(homedir(), '.tonty-tech-interviews', 'recordings')

/** Strip ANSI escape sequences (SGR colors, cursor moves, OSC title sequences, etc).
 *  Keeps printable text intact. CRITICAL for TUI-based CLIs where raw output
 *  is 90%+ control sequences. */
const ESC = String.fromCharCode(27)
const BEL = String.fromCharCode(7)
const C0_EXCEPT_WHITESPACE = Array.from({ length: 32 }, (_, i) =>
  i === 9 || i === 10 || i === 13 ? '' : String.fromCharCode(i),
).join('')

function stripAnsi(str: string): string {
  // ECMA-48 CSI: ESC [ param-bytes(0x30-0x3F) intermediate(0x20-0x2F) final(0x40-0x7E)
  return str
    .replace(new RegExp(`${ESC}\\[[0-?]*[ -/]*[@-~]`, 'g'), '')
    .replace(new RegExp(`${ESC}\\][^${BEL}]*${BEL}`, 'g'), '')
    .replace(new RegExp(`[${C0_EXCEPT_WHITESPACE}]`, 'g'), '')
}

async function ensureRecordingsDir(): Promise<void> {
  if (!existsSync(RECORDINGS_DIR)) {
    await mkdir(RECORDINGS_DIR, { recursive: true })
  }
}

export class SessionRecorder {
  private readonly events: SessionEvent[] = []
  private readonly startTime: number
  private readonly startedAt: string

  constructor() {
    this.startTime = Date.now()
    this.startedAt = new Date().toISOString()
  }

  record(type: SessionEventType, data: string): void {
    // Strip ANSI sequences from stdout/stderr events (TUI tools produce 90%+ control chars)
    const clean = type === 'stdout' || type === 'stderr' ? stripAnsi(data) : data
    // Drop pure-ANSI-noise output events, but NEVER drop stdin events:
    // an Enter keypress arrives as a lone CR, and dropping whitespace-only
    // stdin chunks silently erased every Enter — prompt counting and the
    // transcript's turn detection both depend on them.
    if (type !== 'stdin' && !clean.trim()) return

    this.events.push({
      timestamp: Date.now() - this.startTime,
      type,
      data: clean,
    })
  }

  getEvents(): ReadonlyArray<SessionEvent> {
    return this.events
  }

  toJSON(sessionId: string): RecordingData {
    return {
      sessionId,
      startedAt: this.startedAt,
      events: [...this.events],
    }
  }

  static fromJSON(data: RecordingData): SessionRecorder {
    const recorder = new SessionRecorder()
    Object.defineProperty(recorder, 'startedAt', { value: data.startedAt })
    Object.defineProperty(recorder, 'startTime', {
      value: new Date(data.startedAt).getTime(),
    })
    for (const event of data.events) {
      recorder.events.push({ ...event })
    }
    return recorder
  }

  async save(sessionId: string): Promise<void> {
    await ensureRecordingsDir()
    const filePath = join(RECORDINGS_DIR, `${sessionId}.json`)
    const data = this.toJSON(sessionId)
    await writeFile(filePath, JSON.stringify(data, null, 2))
  }

  static async load(sessionId: string): Promise<SessionRecorder> {
    const filePath = join(RECORDINGS_DIR, `${sessionId}.json`)
    const raw = await readFile(filePath, 'utf-8')
    const data = JSON.parse(raw) as RecordingData
    return SessionRecorder.fromJSON(data)
  }

  static async list(): Promise<string[]> {
    await ensureRecordingsDir()
    const files = await readdir(RECORDINGS_DIR)
    return files
      .filter((f) => f.endsWith('.json'))
      .map((f) => f.replace(/\.json$/, ''))
      .sort()
  }

  /**
   * Bridge parent process stdin/stdout to a PTY for full terminal interaction.
   * This gives the child process a real TTY, enabling interactive TUI mode.
   *
   * - Forwards process.stdin keystrokes → pty.write()
   * - Forwards pty.on('data') output → process.stdout.write() + recording
   * - Handles terminal resize via SIGWINCH
   * - Returns a cleanup function to restore terminal state
   */
  static bridgePTY(pty: IPty, recorder: SessionRecorder): () => void {
    if (!process.stdin.isTTY) {
      // Non-TTY parent: still pipe pty output to stdout + recorder
      pty.onData((data: string) => {
        recorder.record('stdout', data)
        process.stdout.write(data)
      })
      return () => {}
    }

    process.stdin.setRawMode?.(true)
    process.stdin.resume()

    // Buffer stdin for 200ms after setRawMode to absorb terminal escape-code
    // flush. When a terminal switches to raw mode, it sends pending VT queries
    // (color reports, cursor position, device attributes) that should NOT be
    // forwarded to the child PTY. Without this buffer, those bytes are
    // interpreted as user input and can prematurely answer interactive prompts
    // (e.g., Codex's "Do you trust this directory?") before the launcher has
    // a chance to inject the initial instruction.
    let stdinLive = false
    setTimeout(() => {
      stdinLive = true
    }, 200)

    const onData = (chunk: Buffer) => {
      if (!stdinLive) return
      recorder.record('stdin', chunk.toString('utf-8'))
      pty.write(chunk.toString('utf-8'))
    }

    process.stdin.on('data', onData)
    process.stdin.on('end', () => pty.write('\x04')) // Ctrl+D

    pty.onData((data: string) => {
      recorder.record('stdout', data)
      process.stdout.write(data)
    })

    let resizeHandler: (() => void) | undefined
    try {
      pty.resize(process.stdout.columns ?? 80, process.stdout.rows ?? 24)
      resizeHandler = () => {
        pty.resize(process.stdout.columns ?? 80, process.stdout.rows ?? 24)
      }
      process.stdout.on('resize', resizeHandler)
    } catch {
      // resize not supported on all platforms
    }

    const onSignal = () => {
      // SIGINT/SIGTERM: let the PTY handle it
    }
    process.on('SIGINT', onSignal)
    process.on('SIGTERM', onSignal)

    // Return cleanup function
    return () => {
      process.stdin.removeListener('data', onData)
      process.stdin.setRawMode?.(false)
      process.stdin.pause()
      process.removeListener('SIGINT', onSignal)
      process.removeListener('SIGTERM', onSignal)
      if (resizeHandler) {
        process.stdout.removeListener('resize', resizeHandler)
      }
    }
  }
}
